import React, { useState, useEffect } from 'react';
import {
    Container, Box, Typography, Paper, TextField, Button, Card, CardMedia,
    IconButton, Chip, CircularProgress, Autocomplete, Avatar, FormHelperText,
    useTheme, useMediaQuery
} from '@mui/material';
import {
    PostAdd as PostAddIcon, CloudUpload as CloudUploadIcon, Close as CloseIcon,
    Image as ImageIcon, VideoFile as VideoFileIcon, Add as AddIcon, LocalOffer as TagIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import contentService from '../services/contentService';
import productService from '../services/productService';
import '../styles/ProtectedRoutes.css';

const goldFieldSx = {
    '& .MuiOutlinedInput-root': {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        '& fieldset': { borderColor: 'rgba(188, 150, 94, 0.3)' },
        '&:hover fieldset': { borderColor: 'rgba(188, 150, 94, 0.5)' },
        '&.Mui-focused fieldset': { borderColor: '#bc965e' },
    },
    '& .MuiInputLabel-root': {
        color: 'rgba(255, 255, 255, 0.7)',
        '&.Mui-focused': { color: '#bc965e' },
    },
    '& .MuiOutlinedInput-input': { color: 'white' },
};

// error variant overrides the border to red
const goldFieldErrorSx = {
    ...goldFieldSx,
    '& .MuiOutlinedInput-root': {
        ...goldFieldSx['& .MuiOutlinedInput-root'],
        '& fieldset': { borderColor: '#d32f2f' },
        '&:hover fieldset': { borderColor: '#d32f2f' },
        '&.Mui-focused fieldset': { borderColor: '#d32f2f' },
    },
};

const AdminCreatePostSimple = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [formData, setFormData] = useState({ title: '', caption: '', tags: [] });
    const [selectedFile, setSelectedFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);
    const [contentType, setContentType] = useState('');
    const [loading, setLoading] = useState(false);
    const [tagInput, setTagInput] = useState('');
    const [products, setProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [errors, setErrors] = useState({});   // ← field-level errors

    useEffect(() => { loadProducts(); }, []);

    const clearError = (field) => setErrors(prev => ({ ...prev, [field]: '' }));

    const loadProducts = async () => {
        try {
            setLoadingProducts(true);
            const response = await productService.getCustomerProducts({}, 200, 0);
            if (response.success) {
                const readyMade = response.data.filter(p => {
                    const cat = (p.Category || '').trim().toLowerCase();
                    return cat === 'ready made' || cat === 'ready-made';
                });
                setProducts(readyMade);
            } else {
                toast.error('Failed to load products');
            }
        } catch (error) {
            console.error('Failed to load products:', error);
            toast.error('Failed to load products. Please try again.');
        } finally {
            setLoadingProducts(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        clearError(field);
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        if (file.size > 50 * 1024 * 1024) { toast.error('File size must be less than 50MB'); return; }

        const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        const validVideoTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/avi', 'video/mov'];

        if (validImageTypes.includes(file.type)) setContentType('image');
        else if (validVideoTypes.includes(file.type)) setContentType('video');
        else { toast.error('Invalid file type. Please select an image or video file.'); return; }

        setSelectedFile(file);
        clearError('media');
        const reader = new FileReader();
        reader.onload = (e) => setFilePreview(e.target.result);
        reader.readAsDataURL(file);
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        setFilePreview(null);
        setContentType('');
        const fileInput = document.getElementById('file-upload');
        if (fileInput) fileInput.value = '';
    };

    const handleAddTag = () => {
        if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
            setFormData(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
            setTagInput('');
        }
    };

    const handleRemoveTag = (tagToRemove) =>
        setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tagToRemove) }));

    const getProductImageUrl = (photo) => {
        if (!photo) return '';
        if (photo.startsWith('http')) return photo;
        const backendUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
        return `${backendUrl}${photo}`;
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.title.trim())        newErrors.title    = 'Title is required';
        if (!formData.caption.trim())      newErrors.caption  = 'Caption is required';
        if (!selectedFile)                 newErrors.media    = 'Please select a media file';
        if (selectedProducts.length === 0) newErrors.products = 'Please tag at least one product';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validate()) return;

        setLoading(true);
        try {
            const postData = new FormData();
            postData.append('title',       formData.title);
            postData.append('caption',     formData.caption);
            postData.append('contentType', contentType);
            postData.append('tags',        JSON.stringify(formData.tags));
            postData.append('products',    JSON.stringify(selectedProducts.map(p => p.ProductCode)));
            postData.append('mediaFile',   selectedFile);

            const response = await contentService.createPost(postData);
            if (response.success) {
                toast.success('Post created successfully!');
                setFormData({ title: '', caption: '', tags: [] });
                setSelectedFile(null);
                setFilePreview(null);
                setContentType('');
                setSelectedProducts([]);
                setErrors({});
                const fileInput = document.getElementById('file-upload');
                if (fileInput) fileInput.value = '';
            } else {
                throw new Error(response.message || 'Failed to create post');
            }
        } catch (error) {
            console.error('Error creating post:', error);
            toast.error('Failed to create post. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const renderMediaPreview = () => {
        if (!filePreview) return null;
        if (contentType === 'image') {
            return (
                <CardMedia component="img" image={filePreview} alt="Preview"
                    sx={{ height: isMobile ? 140 : 180, width: '100%', objectFit: 'cover', borderRadius: 2 }} />
            );
        }
        return (
            <CardMedia component="video" src={filePreview} controls
                sx={{ height: isMobile ? 140 : 180, width: '100%', objectFit: 'cover', borderRadius: 2 }} />
        );
    };

    return (
        <Container maxWidth="md" sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1.5, sm: 2 } }}>

            {/* Header */}
            <Box sx={{ mb: { xs: 1.5, sm: 2 }, textAlign: 'center' }}>
                <Typography
                    variant={isMobile ? 'h5' : 'h4'}
                    sx={{
                        fontWeight: 700,
                        background: 'linear-gradient(45deg, #bc965e, #d4af37)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 0.5,
                    }}
                >
                    Create New Post
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 300 }}>
                    Share your latest fashion creations with the world
                </Typography>
            </Box>

            <form onSubmit={handleSubmit}>
                <Paper elevation={0} sx={{
                    p: { xs: 1.5, sm: 2, md: 2.5 },
                    background: 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(188, 150, 94, 0.74)',
                    borderRadius: 2,
                }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 1.75 } }}>

                        {/* Title */}
                        <TextField
                            fullWidth
                            label="Post Title"
                            value={formData.title}
                            onChange={(e) => handleInputChange('title', e.target.value)}
                            placeholder="Enter an engaging title for your post"
                            size="small"
                            error={!!errors.title}
                            helperText={errors.title}
                            sx={errors.title ? goldFieldErrorSx : goldFieldSx}
                            FormHelperTextProps={{ sx: { color: '#d32f2f' } }}
                        />

                        {/* Caption */}
                        <TextField
                            fullWidth
                            label="Caption"
                            value={formData.caption}
                            onChange={(e) => handleInputChange('caption', e.target.value)}
                            multiline
                            rows={2}
                            placeholder="Write a captivating caption that tells your story..."
                            size="small"
                            error={!!errors.caption}
                            helperText={errors.caption}
                            sx={errors.caption ? goldFieldErrorSx : goldFieldSx}
                            FormHelperTextProps={{ sx: { color: '#d32f2f' } }}
                        />

                        {/* Upload Media */}
                        <Box>
                            <Typography variant="body2" sx={{ color: 'white', mb: 1, fontWeight: 600 }}>
                                Upload Media
                            </Typography>

                            {!selectedFile ? (
                                <Box
                                    onClick={() => document.getElementById('file-upload').click()}
                                    sx={{
                                        border: `2px dashed ${errors.media ? '#d32f2f' : 'rgba(188,150,94,0.5)'}`,
                                        borderRadius: 3,
                                        p: { xs: 1.5, sm: 2 },
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        background: errors.media ? 'rgba(211,47,47,0.05)' : 'rgba(188,150,94,0.05)',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            borderColor: errors.media ? '#d32f2f' : '#bc965e',
                                            backgroundColor: errors.media ? 'rgba(211,47,47,0.08)' : 'rgba(188,150,94,0.1)',
                                        },
                                    }}
                                >
                                    <input id="file-upload" type="file" accept="image/*,video/*"
                                        onChange={handleFileSelect} style={{ display: 'none' }} />
                                    <CloudUploadIcon sx={{ fontSize: { xs: 28, sm: 36 }, color: errors.media ? '#d32f2f' : '#bc965e', mb: 0.5 }} />
                                    <Typography variant="body1" sx={{ color: 'white', fontWeight: 600, mb: 0.25 }}>
                                        {isMobile ? 'Tap to upload' : 'Click to browse'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                        {isMobile ? 'Images & Videos • Max 50MB' : 'JPG, PNG, GIF, WebP, MP4, WebM, MOV • Max 50MB'}
                                    </Typography>
                                </Box>
                            ) : (
                                <Card elevation={0} sx={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(188,150,94,0.2)', borderRadius: 3 }}>
                                    <Box sx={{ position: 'relative' }}>
                                        <IconButton onClick={handleRemoveFile} size="small"
                                            sx={{
                                                position: 'absolute', top: 6, right: 6, zIndex: 1,
                                                backgroundColor: 'rgba(0,0,0,0.7)', color: 'white',
                                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.9)' },
                                            }}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                        <Box sx={{ p: 1 }}>{renderMediaPreview()}</Box>
                                    </Box>
                                    <Box sx={{ p: 1.25 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                                            {contentType === 'image'
                                                ? <ImageIcon sx={{ color: '#bc965e', fontSize: 18 }} />
                                                : <VideoFileIcon sx={{ color: '#bc965e', fontSize: 18 }} />
                                            }
                                            <Typography variant="body2"
                                                sx={{ color: 'white', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {selectedFile.name}
                                            </Typography>
                                        </Box>
                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                            {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB • {contentType.toUpperCase()}
                                        </Typography>
                                    </Box>
                                </Card>
                            )}

                            {/* inline media error */}
                            {errors.media && (
                                <FormHelperText sx={{ color: '#d32f2f', mt: 0.5, ml: 0.5 }}>
                                    {errors.media}
                                </FormHelperText>
                            )}
                        </Box>

                        {/* Tags */}
                        <Box>
                            <Typography variant="body2" sx={{ color: 'white', mb: 1, fontWeight: 600 }}>
                                Tags
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                <TextField
                                    size="small"
                                    label="Add Tag"
                                    value={tagInput}
                                    onChange={(e) => setTagInput(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); } }}
                                    placeholder="fashion, style, trending..."
                                    sx={{ flex: 1, ...goldFieldSx }}
                                />
                                <Button onClick={handleAddTag} variant="outlined" size="small"
                                    sx={{
                                        minWidth: 40, px: 1.25, flexShrink: 0,
                                        borderColor: 'rgba(188,150,94,0.5)', color: '#bc965e',
                                        '&:hover': { borderColor: '#bc965e', backgroundColor: 'rgba(188,150,94,0.1)' },
                                    }}>
                                    <AddIcon fontSize="small" />
                                </Button>
                            </Box>
                            {formData.tags.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {formData.tags.map((tag, index) => (
                                        <Chip key={index} label={`#${tag}`} onDelete={() => handleRemoveTag(tag)}
                                            deleteIcon={<CloseIcon />} size="small"
                                            sx={{
                                                backgroundColor: 'rgba(188,150,94,0.2)', color: 'white',
                                                border: '1px solid rgba(188,150,94,0.5)',
                                                '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } },
                                            }} />
                                    ))}
                                </Box>
                            )}
                        </Box>

                        {/* Tag Products */}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <TagIcon sx={{ color: errors.products ? '#d32f2f' : '#bc965e', fontSize: 18 }} />
                                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                                    Tag Products *
                                </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1, display: 'block' }}>
                                Tag products from our collection featured in this post
                            </Typography>

                            <Autocomplete
                                multiple
                                size="small"
                                options={products}
                                getOptionLabel={(option) => `${option.ProductName} - ${option.ProductCode} - ${option.Color || 'N/A'}`}
                                value={selectedProducts}
                                onChange={(event, newValue) => {
                                    setSelectedProducts(newValue);
                                    if (newValue.length > 0) clearError('products');
                                }}
                                loading={loadingProducts}
                                filterOptions={(options, { inputValue }) => {
                                    const s = inputValue.toLowerCase();
                                    return options.filter(o =>
                                        o.ProductName.toLowerCase().includes(s) ||
                                        o.ProductCode.toLowerCase().includes(s) ||
                                        (o.Color && o.Color.toLowerCase().includes(s))
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Search by name, code, or color..."
                                        size="small"
                                        error={!!errors.products}
                                        sx={errors.products ? goldFieldErrorSx : goldFieldSx}
                                        slotProps={{
                                            input: {
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {loadingProducts ? <CircularProgress size={16} sx={{ color: '#bc965e' }} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.75, px: 1.25 }}>
                                        <Avatar src={getProductImageUrl(option.Photo)} alt={option.ProductName} variant="rounded"
                                            sx={{ width: 40, height: 40, flexShrink: 0 }} />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" sx={{ color: 'white', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {option.ProductName}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block' }}>
                                                {option.ProductCode}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25, alignItems: 'center', flexWrap: 'wrap' }}>
                                                {option.Color && (
                                                    <Chip label={option.Color} size="small"
                                                        sx={{ height: 16, fontSize: '0.6rem', backgroundColor: 'rgba(188,150,94,0.2)', color: 'white' }} />
                                                )}
                                                <Typography variant="caption" sx={{ color: '#bc965e', fontWeight: 600 }}>₹{option.Price}</Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            {...getTagProps({ index })}
                                            key={option.ProductCode}
                                            avatar={<Avatar src={getProductImageUrl(option.Photo)} />}
                                            label={isMobile ? option.ProductCode : `${option.ProductName} (${option.ProductCode})`}
                                            size="small"
                                            sx={{
                                                backgroundColor: 'rgba(188,150,94,0.2)', color: 'white',
                                                border: '1px solid rgba(188,150,94,0.5)',
                                                maxWidth: { xs: 130, sm: 'none' },
                                                '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } },
                                            }}
                                        />
                                    ))
                                }
                                sx={{
                                    '& .MuiAutocomplete-popupIndicator': { color: '#bc965e' },
                                    '& .MuiAutocomplete-clearIndicator': { color: '#bc965e' },
                                }}
                            />

                            {/* inline products error */}
                            {errors.products && (
                                <FormHelperText sx={{ color: '#d32f2f', mt: 0.5, ml: 0.5 }}>
                                    {errors.products}
                                </FormHelperText>
                            )}
                        </Box>

                        {/* Submit */}
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PostAddIcon />}
                            disabled={loading}
                            size="medium"
                            sx={{
                                py: 1,
                                fontSize: '0.95rem',
                                fontWeight: 600,
                                background: '#a88752 !important',
                                '&:hover': {
                                    background: 'linear-gradient(45deg, #a8834d, #c19b2a)',
                                    boxShadow: '0 8px 25px rgba(188,150,94,0.3)',
                                },
                                '&:disabled': { background: 'rgba(188,150,94,0.3)', color: 'rgba(255,255,255,0.5)' },
                                transition: 'all 0.3s ease',
                            }}
                        >
                            {loading ? 'Creating Your Post...' : 'Publish Post'}
                        </Button>

                    </Box>
                </Paper>
            </form>
        </Container>
    );
};

export default AdminCreatePostSimple;